import React, { useMemo, useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import { Modal, ModalBody, Spinner } from "reactstrap";
import Text from "components/Text";
import crossIcon from "../../../../assets/images/cross.svg";
import searchIcon from "assets/images/search-icon.svg";
import StaffDefaultImage from "assets/images/staff-default-rounded.png";
import { useAllActiveOffices } from "repositories/scheduler-repository";
import styles from "../OfficePolicyManagement.module.scss";
import constants from "../../../../constants";

const SelectOfficesModal = ({
  t,
  isOpen,
  closeModal,
  ownerId,
  offices: officesProp = [],
  selectedOfficeIds = {},
  onSelectionChange,
  onApply,
}) => {
  const [search, setSearch] = useState("");
  const [displayList, setDisplayList] = useState([]);

  const { isLoading: officeDataLoading, data: officeData } =
    useAllActiveOffices(
      1,
      constants.ACTIVE_OFFICES_PAGE_SIZE,
      ownerId ? { ownerId, includeAdminOffice: true } : null,
      "",
      { enabled: isOpen && !!ownerId },
    );

  const officeList = useMemo(() => {
    if (ownerId && officeData?.data?.length) {
      return officeData.data
        .map((o) => ({
          id: o.id ?? o.officeId,
          name: o.name ?? o.officeName ?? "",
          officeImage: o.officeImage ?? o.image ?? null,
        }))
        .filter((o) => o.id != null);
    }
    const list = Array.isArray(officesProp) ? officesProp : [];
    return list
      .map((o) => ({
        id: o.id ?? o.officeId,
        name: o.name ?? o.officeName ?? "",
        officeImage: o.officeImage ?? o.image ?? null,
      }))
      .filter((o) => o.id != null);
  }, [ownerId, officeData, officesProp]);

  useEffect(() => {
    if (!search.trim()) {
      setDisplayList(officeList);
      return;
    }
    const term = search.trim().toLowerCase().replace(/\s+/g, "");
    const filtered = officeList.filter((item) => {
      const fullName = (item.name || "").replace(/\s+/g, "").toLowerCase();
      return fullName.includes(term);
    });
    setDisplayList(filtered);
  }, [search, officeList]);

  const selectedCount = useMemo(() => {
    return Object.keys(selectedOfficeIds).filter((id) => selectedOfficeIds[id])
      .length;
  }, [selectedOfficeIds]);

  const allSelected = useMemo(() => {
    if (displayList.length === 0) return false;
    return displayList.every((o) => selectedOfficeIds[o.id]);
  }, [displayList, selectedOfficeIds]);

  const handleSelectAll = (e) => {
    const checked = e.currentTarget.checked;
    const next = { ...selectedOfficeIds };
    displayList.forEach((o) => {
      next[o.id] = checked;
    });
    onSelectionChange(next);
  };

  const handleOfficeToggle = (e, office) => {
    const isChecked = e.currentTarget.checked;
    onSelectionChange({
      ...selectedOfficeIds,
      [office.id]: isChecked,
    });
  };

  const handleApply = () => {
    const selectedList = officeList.filter((o) => selectedOfficeIds[o.id]);
    const isAllOffice =
      officeList.length > 0 && selectedList.length === officeList.length;
    onApply(selectedOfficeIds, selectedList, isAllOffice);
    closeModal();
  };

  useEffect(() => {
    if (isOpen) {
      setSearch("");
    }
  }, [isOpen]);

  const isLoading = ownerId ? officeDataLoading : false;

  return (
    <Modal
      isOpen={isOpen}
      toggle={closeModal}
      className="modal-dialog-centered modal-width-660 new-group-modal"
      modalClassName="custom-modal"
    >
      <span className="close-btn" onClick={closeModal}>
        <img src={crossIcon} alt="close" />
      </span>
      <ModalBody>
        <Text size="25px" marginBottom="10px" weight="500" color="#111b45">
          {t("officePolicyManagement.selectOffices")}
        </Text>

        {isLoading && (
          <div className="loader-container">
            <Spinner animation="border" className="loader-spinner" />
          </div>
        )}

        <div className={"search-box w-100 " + styles["search-box"]}>
          <input
            type="text"
            placeholder={t("accountOwner.searchByName")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="ico">
            <img src={searchIcon} alt="icon" />
          </span>
        </div>

        <Text
          size="12px"
          marginBottom="20px"
          marginTop="20px"
          weight="400"
          color="rgb(111, 119, 136)"
        >
          {selectedCount} {t("officePolicyManagement.selected")}
        </Text>

        {displayList.length > 0 && (
          <div className="ch-checkbox c-field all-event-checkbox m-0">
            <label>
              <input
                type="checkbox"
                onChange={handleSelectAll}
                checked={allSelected}
              />
              <span>{t("officePolicyManagement.selectAll")}</span>
            </label>
          </div>
        )}

        <ul
          className="modal-employee-list group-list"
          id="scrollableDiv"
          style={{
            maxHeight: 320,
            overflowY: "auto",
            paddingLeft: 0,
            listStyle: "none",
          }}
        >
          {displayList.length > 0
            ? displayList.map((item) => (
                <li key={item.id}>
                  <div className={styles.border_list + " ch-checkbox"}>
                    <label>
                      <input
                        type="checkbox"
                        checked={!!selectedOfficeIds[item.id]}
                        onChange={(e) => handleOfficeToggle(e, item)}
                      />
                      <span className="d-flex align-items-center">
                        <img
                          src={item.officeImage || StaffDefaultImage}
                          alt="office"
                          className={styles.Default_staff_image}
                        />
                        <p className="m-0 ml-2">{item.name}</p>
                      </span>
                    </label>
                  </div>
                </li>
              ))
            : !isLoading && (
                <li>
                  <div className="ch-checkbox">
                    <label>
                      {t("officePolicyManagement.noOfficeAvailable")}
                    </label>
                  </div>
                </li>
              )}
        </ul>

        <button
          type="button"
          className="button button-round button-shadow mr-sm-3 mb-3 w-sm-100 mt-4"
          title={t("officePolicyManagement.select")}
          onClick={handleApply}
        >
          {t("officePolicyManagement.select")}
        </button>
        <button
          type="button"
          className="button button-round button-border btn-mobile-link button-dark mb-md-3 mt-md-4"
          onClick={closeModal}
          title={t("cancel")}
        >
          {t("officePolicyManagement.cancel")}
        </button>
      </ModalBody>
    </Modal>
  );
};

export default withTranslation()(SelectOfficesModal);
